import {
  Header,
  Sidenav,
  type SidenavNavItem,
  AdminIcon,
  DashboardIcon,
  IdentitiesIcon,
  IntegrationsIcon,
  PolicyIcon,
} from "@repo/ui";
import { ReactNode, useMemo } from "react";
import { useIntl } from "react-intl";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useViewport, ViewportProvider } from "../contexts/ViewportContext";
import { useGetCurrentUserRoles } from "./hooks/useGetCurrentUserRoles";
import { navLabels } from "./navLabels";

interface ShellLayoutProps {
  children: ReactNode;
}

function ShellLayoutInner({ children }: ShellLayoutProps) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const intl = useIntl();
  const {
    isMenuPinned,
    isMenuExpanded,
    setMenuPinned,
    setMenuExpanded,
    headerRef,
  } = useViewport();

  const { data, isLoading } = useGetCurrentUserRoles();
  const isAdmin = data?.roles?.includes("ROLE_SAV_ISPMADMIN") ?? false;

  const navItems = useMemo<SidenavNavItem[]>(
    () => [
      {
        navItem: "dashboard",
        label: intl.formatMessage(navLabels.dashboard),
        icon: <DashboardIcon />,
        section: "workspace",
      },
      {
        navItem: "identities",
        label: intl.formatMessage(navLabels.identities),
        icon: <IdentitiesIcon />,
        section: "workspace",
      },
      {
        navItem: "integrations",
        label: intl.formatMessage(navLabels.integrations),
        icon: <IntegrationsIcon />,
        section: "workspace",
      },
      {
        navItem: "policies",
        label: intl.formatMessage(navLabels.policies),
        icon: <PolicyIcon />,
        section: "workspace",
      },
      ...(isAdmin
        ? [
            {
              navItem: "admin",
              label: intl.formatMessage(navLabels.admin),
              icon: <AdminIcon />,
              section: "system" as const,
              subNav: [
                {
                  subNavItem: "ispm-users",
                  label: intl.formatMessage(navLabels["ispm-users"]),
                },
                {
                  subNavItem: "settings",
                  label: intl.formatMessage(navLabels.settings),
                  children: [
                    {
                      subNavItem: "sso-settings",
                      label: intl.formatMessage(navLabels["sso-settings"]),
                    },
                  ],
                },
              ],
            },
          ]
        : []),
    ],
    [intl, isAdmin],
  );

  return (
    <div className="min-h-screen flex bg-page-bg">
      <div className="sticky top-0 self-start h-screen shrink-0">
        <Sidenav
          navItems={navItems}
          isLoading={isLoading}
          isMenuPinned={isMenuPinned}
          onPinnedChange={setMenuPinned}
          isMenuExpanded={isMenuExpanded}
          onExpandedChange={setMenuExpanded}
        />
      </div>
      <div
        className="flex min-w-0 flex-1 flex-col"
        style={{ background: "var(--color-page-subtle)" }}
      >
        <div
          ref={headerRef}
          className="sticky top-0 z-[1008] shrink-0 bg-[var(--color-background-neutral-subtlest)]"
        >
          <Header
            theme={theme}
            onToggleTheme={toggleTheme}
            onLogout={logout}
            showLogo={!(isMenuPinned || isMenuExpanded)}
            showHeaderActions={false}
          />
        </div>
        <main className="min-w-0 flex-1 pb-8">{children}</main>
      </div>
    </div>
  );
}

export function ShellLayout({ children }: ShellLayoutProps) {
  return (
    <ViewportProvider>
      <ShellLayoutInner>{children}</ShellLayoutInner>
    </ViewportProvider>
  );
}
